import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  ElementRef,
  OnDestroy,
  AfterViewChecked,
} from '@angular/core';
import { TaskDataService } from '../../../shared/services/task-data.service';
import {
  ITreasuryRequestData,
  TreasuryRequestData,
  ITaskRequest,
  TaskRequest,
  IGeneratedTask,
} from '../../../modules/tasks/model/types';
import notify from 'devextreme/ui/notify';
import validationEngine from 'devextreme/ui/validation_engine';
import {
  getNotificationTimeout,
  sendNotificationMessage,
} from '../../../types/resource';
import { LoggingService } from '../../../framework/logging/logging.service';
import { SeverityLevel } from '../../../framework/logging/severity-level.model';
import { lastValueFrom, Observable, BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { MessageService } from '../../../shared/components/framework/services/message.service';

import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { IPrototypeGenerationRow } from './types/fandc-generator.types';

import { select, Store } from '@ngrx/store';
import { IFandCGeneratorState } from './types/fandc-generator.state';
import {
  selectEntityNameOptions,
  selectFormatOptions,
  selectFrequencyOffsetOptions,
  selectFrequencyOptions,
  selectGeneratedTasks,
  selectLoans,
  selectMaturityDate,
  selectedLoanNumber,
  selectReferenceDate,
  selectReportEntityOptions,
  selectSelectedDefaultTasks,
  selectSelectedEntityName,
  selectSelectedLoanDetail,
  selectSelectedReportEntity,
  selectTemplates,
  selectRootTemplate,
  selectSelectedTemplateType,
  selectCategory,
  selectedFrequency,
  selectFrequencyOffsetValue,
  selectSelectedFormatOption,
} from './store/fandc-generator.selectors';
import {
  fetchDefaultTasksForTemplate,
  fetchLoanDetail,
  fetchTemplates,
  fetchTemplateTypes,
  generateTasks,
  resetFandCGenerator,
  setLoanNumber,
  setMaturityDate,
  setReferenceDate,
  setFrequency,
  updatePrototypeTasks,
  updateSelectedDefaultTasks,
  fetchFrequencyOptions,
  setFrequencyOffsetValue,
  fetchLoans,
  fetchFormatOptions,
  setFormatOption,
  setReportEntity,
  setEntityName,
  fetchReportEntityOptions,
  updateGeneratedTasks,
  saveGeneratedTasks,
  fetchGeneratedTasksSuccess,
  saveGeneratedTasksSuccess,
  fetchLoansSuccess,
  saveGenerationTask,
  fetchRootTemplate,
  addToSelectedPrototypeTasks,
  removePrototypeTask,
  setSelectedPrototypeTasks,
  saveGenerationMetadata,
  fetchGenerationMetadata,
  fetchGenerationMetadataSuccess,
  saveGenerationTaskSuccess,
  setTaskId,
  setCategory,
} from './store/fandc-generator.actions';
import {
  selectTemplateTypes,
  selectDefaultTasksForTemplate,
  selectSelectedPrototypeTasks,
} from './store/fandc-generator.selectors';
import { FrequencyOptionsService } from './services/frequencyoptions.service';
import { Actions, ofType } from '@ngrx/effects';
import saveAs from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';

@Component({
  selector: 'app-fandc-generator',
  templateUrl: './fandc-generator.component.html',
  styleUrls: ['./fandc-generator.component.scss'],
})
export class FandCGeneratorComponent implements OnInit, OnDestroy {
  @ViewChild('formElement') formElement!: ElementRef;
  @ViewChild('scrollView', { static: false }) scrollView: DxScrollViewComponent;
  @ViewChild('generatedTasksElement', { static: false })
  generatedTasksElementDiv: ElementRef;
  @ViewChild('prototypeTasksGrid', { static: false }) prototypeTasksGrid: DxDataGridComponent;

  @Input() taskTemplateName: string;
  @Input() taskGenerationMetadata: any;
  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  treasuryRequestTypes: any[];
  bankData: any[];
  fandCRequestData: ITreasuryRequestData;
  loanDisplayText: string = '';
  isLoadingLoanData = false;
  isLoadingMetadata = false;
  isSavingGenerationTask = false;
  isGeneratingTasks = false;
  isSavingTasks = false;
  isLoanNumberPopulated = false; // Track if loan number is populated
  isTaskTypesSelected = false; // Track if task types are selected
  shouldScrollToGeneratedTasks = false;
  files: File[] = [];
  requestTypes: any[];
  prototypeGenerationData$: Observable<any[]>;
  maturityDate$: Observable<Date | null>;
  referenceDate$: Observable<Date | null>;
  loanData$: Observable<any[]>;
  filteredLoanData$: Observable<any[]>;
  templateTypeData$: Observable<any>;
  templateData$: Observable<any>;
  frequencyOptions$: Observable<any>;
  frequencyOffsetOptions$: Observable<any>;
  formatOptions$: Observable<any>;
  defaultTasksForTemplate$: Observable<any>;
  selectedDefaultTasks$: Observable<any>;

  selectedPrototypeTasksSubscription: Subscription;
  generatedTasksSubscription: Subscription;
  loanNumberSubscription: Subscription;
  defaultTasksSubscription: Subscription;
  templateTypeSubscription: Subscription;
  templateDataSubscription: Subscription;
  defaultTasksForTemplateSubscription: Subscription;
  frequencyOptionsSubscription: Subscription;
  frequencyOffsetOptionsSubscription: Subscription;
  maturityDateSubscription: Subscription;
  formatOptionsSubscription: Subscription;
  reportEntityOptionsSubscription: Subscription;
  entityNameOptionsSubscription: Subscription;
  loanDataSubscription: Subscription;
  referenceDateSubscription: Subscription;
  loadingActionsSubscription: Subscription;

  // Value sync subscriptions for form fields
  templateTypeValueSubscription: Subscription;
  categoryValueSubscription: Subscription;
  frequencyValueSubscription: Subscription;
  frequencyOffsetValueSubscription: Subscription;
  formatValueSubscription: Subscription;
  reportEntityValueSubscription: Subscription;
  entityNameValueSubscription: Subscription;

  selectedPrototypeTasks$: Observable<any>;
  selectedPrototypeTasks: IPrototypeGenerationRow[] = [];
  generatedTasks: IGeneratedTask[] = [];

  selectedTemplateType: string | null = null;
  selectedTemplateType$ = new BehaviorSubject<string | null>(null);
  selectedTemplate: string | null = null;
  selectedTemplate$ = new BehaviorSubject<string | null>(null);
  selectedDefaultTasks: string[] | null = null;

  reportEntityOptions$: Observable<any>;
  entityNameOptions$: Observable<any>;
  activeLoanCheckboxText: string = "Show Inactive";

  templateTypeEditorOptions: any = {
    items: [], // will be set from your observable
    valueExpr: 'templateType',
    displayExpr: 'templateType',
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    value: null,
    onValueChanged: (e: any) => this.onTemplateTypeChanged(e),
  };

  templateEditorOptions: any = {
    items: [], // will be set from observable
    valueExpr: undefined, // use the string value directly
    displayExpr: undefined, // use the string value directly
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    value: this.selectedTemplate,
    onValueChanged: (e: any) => this.onTemplateChanged(e),
  };

  defaultTasksEditorOptions: any = {
    items: [],
    valueExpr: 'taskId',
    displayExpr: 'taskName',
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    showSelectionControls: true,
    multiline: true,
    onValueChanged: (e: any) => this.onDefaultTasksChanged(e),
  };

  maturityDateOptions: any = {
    value: null, // This will be set from the observable in ngOnInit
    required: true,
    openOnFieldClick: true,
    displayFormat: 'MM/dd/yyyy',
    pickerType: 'calendar',
  };

  referenceDateOptions: any = {
    value: null, // This will be set from the observable in ngOnInit
    required: true,
    openOnFieldClick: true,
    displayFormat: 'MM/dd/yyyy',
    pickerType: 'calendar',
  };


  oneTimeDueDateOptions: any = {
    value: null,
    required: true,
    openOnFieldClick: true,
    displayFormat: 'MM/dd/yyyy',
    pickerType: 'calendar',
    onValueChanged: (e: any) => {
      // Dispatch setFrequencyOffsetValue action with the selected date
      this.store.dispatch(setFrequencyOffsetValue({ selectedFrequencyOffsetValue: e.value }));
    },
  };

  selectedFrequency: string | null = null;

  frequencyEditorOptions: any = {
    items: [], // will be set from observable
    valueExpr: undefined, // use the string value directly
    displayExpr: undefined, // use the string value directly
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    multiline: true,
    value: this.selectedFrequency,
    onValueChanged: (e: any) => {
      // console.log('Frequency changed:', e.value);

      // Update local state
      this.selectedFrequency = e.value;

      // Dispatch action to update store
      this.store.dispatch(setFrequency({ selectedFrequency: e.value }));

      // Force update the UI to reflect the new value
      this.frequencyEditorOptions = {
        ...this.frequencyEditorOptions,
        value: e.value
      };

      // Reset due only for user-initiated changes. Metadata/programmatic updates
      // should preserve the restored due selection.
      if (e?.event && this.selectedFrequencyOffsetValue) {
        this.selectedFrequencyOffsetValue = null;
        this.store.dispatch(setFrequencyOffsetValue({ selectedFrequencyOffsetValue: null }));
      }
    },
  };

  selectedFrequencyOffsetValue: string | Date | null = null;

  frequencyOffsetEditorOptions: any = {
    items: [],
    displayExpr: 'label',
    valueExpr: 'value',
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    multiline: true,
    value: this.selectedFrequencyOffsetValue, // Initialize with the primitive value
    onValueChanged: (e: any) => {
      // Ignore programmatic/initialization changes that can clear metadata-restored values.
      if (!e?.event || e.value == null || e.value === '') {
        return;
      }
      this.store.dispatch(
        setFrequencyOffsetValue({ selectedFrequencyOffsetValue: e.value })
      );
    },
  };

  selectedFormatValue: string | null = null;

  formatEditorOptions: any = {
    items: [],
    displayExpr: undefined,
    valueExpr: undefined,
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    multiline: true,
    value: this.selectedFormatValue,
    onValueChanged: (e: any) => {
      this.store.dispatch(setFormatOption({ selectedFormatOption: e.value }));
    },
  };

  selectedReportEntityValue: string | null = null;

  reportEntityOptions: any = {
    items: [],
    displayExpr: 'label',
    valueExpr: 'value',
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    multiline: true,
    value: this.selectedReportEntityValue,
    onValueChanged: (e: any) => {
      if (!e?.event || e.value == null || e.value === '') {
        return;
      }
      this.store.dispatch(setReportEntity({ selectedReportEntity: e.value }));
    },
  };

  selectedEntityName: string | null = null;
  selectedLoanNumber$: Observable<any>; // Observable for the selected loan
  private availableLoans: any[] = [];
  showInactiveLoans: boolean = false; // Track whether to show inactive loans
  private showInactiveLoansSubject = new BehaviorSubject<boolean>(false);

  entityNameOptions: any = {
    items: [],
    displayExpr: 'label',
    valueExpr: 'label',
    required: true,
    searchMode: 'contains',
    acceptCustomValue: true,
    searchEnabled: true,
    multiline: true,
    value: this.selectedEntityName,
  };

  // Method to handle custom text values in the entityName SelectBox
  onEntityNameCustomItemCreating = (e) => {
    const newItem = {
      label: e.text,
      value: e.text
    };

    // Add the custom item to the existing items
    this.customEntityNameOptions.items = [...this.customEntityNameOptions.items, newItem];

    // Set the new item
    e.customItem = newItem;

    // Dispatch action to update the selected entity name in the store
    this.store.dispatch(setEntityName({ selectedEntityName: e.text }));

    return newItem;
  };

  // Custom entity name options with additional properties for custom value input
  customEntityNameOptions: any = {
    items: [],
    displayExpr: 'label',
    valueExpr: 'label',
    required: true,
    searchMode: 'contains',
    acceptCustomValue: true,
    searchEnabled: true,
    multiline: true,
    value: this.selectedEntityName,
    onValueChanged: (e: any) => {
      this.store.dispatch(setEntityName({ selectedEntityName: e.value }));
    },
    onCustomItemCreating: this.onEntityNameCustomItemCreating.bind(this)
  };

  readonlyLoanNumberEditorOptions: any = {
    value: 'N/A',
    disabled: true,
  };

  constructor(
    private readonly taskDataService: TaskDataService,
    private readonly loggingService: LoggingService,
    private readonly messageService: MessageService,
    private frequencyOptionsService: FrequencyOptionsService,
    private actions$: Actions,
    private store: Store<IFandCGeneratorState>
  ) {

    // Initialize observables
    this.templateTypeData$ = this.store.select(selectTemplateTypes);
    this.templateData$ = this.store.select(selectTemplates);
    this.frequencyOptions$ = this.store.select(selectFrequencyOptions);
    this.formatOptions$ = this.store.select(selectFormatOptions);
    this.frequencyOffsetOptions$ = this.store.select(selectFrequencyOffsetOptions);
    this.defaultTasksForTemplate$ = this.store.select(selectDefaultTasksForTemplate);
    this.selectedDefaultTasks$ = this.store.select(selectSelectedDefaultTasks);
    this.maturityDate$ = this.store.select(selectMaturityDate);
    this.referenceDate$ = this.store.select(selectReferenceDate);
    this.loanData$ = this.store.select(selectLoans);
    this.selectedLoanNumber$ = this.store.select(selectSelectedLoanDetail); // Initialize the selected loan observable with full loan object
    this.reportEntityOptions$ = this.store.select(selectReportEntityOptions);
    this.entityNameOptions$ = this.store.select(selectEntityNameOptions);

    // Create filtered loans observable that responds to checkbox state
    this.filteredLoanData$ = combineLatest([
      this.loanData$,
      this.showInactiveLoansSubject.asObservable()
    ]).pipe(
      map(([loans, showInactive]) => {
        if (!loans) return [];
        return showInactive ? loans : loans.filter(loan => loan.status !== 'Inactive');
      })
    );

    // Set up subscriptions that need immediate activation
    this.selectedPrototypeTasksSubscription = this.store
      .select(selectSelectedPrototypeTasks)
      .subscribe((result) => {
        // console.log('Selected Prototype Tasks:', result);
        const selectedPrototypeTasks = result
          ? result.map((x) => ({ ...x }))
          : [];
        const selectedPrototypeTaskIds = selectedPrototypeTasks
          .map((task) => task?.taskId)
          .filter((taskId) => taskId != null && taskId !== '');
        const selectedPrototypeTaskIdsForView = [...selectedPrototypeTaskIds];
        const selectedPrototypeTaskIdsForState = [...selectedPrototypeTaskIds];

        this.selectedPrototypeTasks = selectedPrototypeTasks;
        this.selectedDefaultTasks = selectedPrototypeTaskIdsForView;
        this.defaultTasksEditorOptions = {
          ...this.defaultTasksEditorOptions,
          value: selectedPrototypeTaskIdsForView,
        };

        this.store.dispatch(
          updateSelectedDefaultTasks({ data: selectedPrototypeTaskIdsForState })
        );

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          taskTypes: selectedPrototypeTaskIdsForView,
        } as any;
      });

    this.generatedTasksSubscription = this.store
      .select(selectGeneratedTasks)
      .subscribe((result) => {
        // console.log('Generated Tasks:', result);
        this.generatedTasks = result ? result.map((x) => ({ ...x })) : [];
      });

    // Subscribe to selected loan to track if it's populated
    this.loanNumberSubscription = this.selectedLoanNumber$.subscribe(loanDetail => {
      this.isLoanNumberPopulated = !!loanDetail;
      this.loanDisplayText =
        loanDetail?.facilityDetail?.name ??
        loanDetail?.facilityDetail?.Name ??
        loanDetail?.name ??
        loanDetail?.Name ??
        '';
      this.readonlyLoanNumberEditorOptions = {
        ...this.readonlyLoanNumberEditorOptions,
        value: this.loanDisplayText || 'N/A',
        disabled: true,
      };
      // Turn off metadata loading when loan data arrives
      if (loanDetail && this.isLoadingMetadata) {
        this.isLoadingMetadata = false;
      }
    });

    // Subscribe to selectedDefaultTasks to track if any are selected
    this.defaultTasksSubscription = this.selectedDefaultTasks$.subscribe(tasks => {
      this.isTaskTypesSelected = tasks && tasks.length > 0;
    });
  }

  onTemplateTypeChanged(e: any) {
    // console.log(e);
    this.selectedTemplateType = e.value;

    this.store.dispatch(
      fetchTemplates({
        criteria: {
          referenceTypeName: 'FandC',
          templateName: this.selectedTemplateType,
        },
      })
    );
    //this.selectedTemplateType$.next(e.value);
  }

  onTemplateChanged(e: any) {
    // console.log(e);
    this.selectedTemplate = e.value;
    // Extract the first word of the selected template
    const firstWord = e.value.split(' ')[0];

    // console.log(firstWord);
    // Check if this word matches any frequency option
    if (this.frequencyEditorOptions && this.frequencyEditorOptions.items) {
      const matchingFrequency = this.frequencyEditorOptions.items.find(
      frequency => frequency.startsWith(firstWord)
      );

      // console.log('Matching Frequency:', matchingFrequency);

      // If there's a match, update the frequency selection
      if (matchingFrequency) {
        this.selectedFrequency = matchingFrequency;
        this.store.dispatch(setFrequency({ selectedFrequency: matchingFrequency }));

        // Update the frequency editor options to reflect the new value on the UI
        this.frequencyEditorOptions = {
          ...this.frequencyEditorOptions,
          value: this.selectedFrequency
        };
      }
    }

    // Dispatch setCategory with the template name to ensure the category is set in the store
    this.store.dispatch(
      fetchDefaultTasksForTemplate({
        criteria:
          {
            templateName: e.value,
          },
      })
    );
  }

  onDefaultTasksChanged(e: any) {
    // console.log(e);
    const selectedItems = Array.isArray(e?.value) ? e.value : [];
    this.selectedDefaultTasks = selectedItems
      .map((item: any) => item?.taskId ?? item)
      .filter((taskId: any) => taskId != null && taskId !== '');
    this.store.dispatch(
      updateSelectedDefaultTasks({ data: this.selectedDefaultTasks })
    );
    // Update the flag indicating whether task types are selected
    this.isTaskTypesSelected = selectedItems.length > 0;
  }

  onPrototypeRowUpdated(e: any) {
    this.store.dispatch(updatePrototypeTasks({ changeData: { ...e.data } }));
  }

  onGeneratedTaskRowUpdated(e: any) {
    this.store.dispatch(updateGeneratedTasks({ changeData: { ...e.data } }));
  }

  onFileUpload(file: File) {
    this.files.push(file);
  }

  removePrototypeTask(taskId: string) {
    this.store.dispatch(removePrototypeTask({ taskId }));
  }

  deletePrototypeTask = (e: any) => {
    const taskId = e.row.data.taskId;
    this.store.dispatch(removePrototypeTask({ taskId }));
  }

  async onSubmit(e: any) {
    e.preventDefault();

    // let validationResult = validationEngine.validateGroup('formGroup');

    // if (validationResult.isValid) {
    //   try {
    //     let request: ITaskRequest = new TaskRequest();
    //     let requestType = this.requestTypes.find(
    //       (t) => t.name == this.fandCRequestData.requestType
    //     );
    //     request.templateId = requestType.id;

    //     request.name = requestType.name;
    //     request.referenceName = this.fandCRequestData.loanNumber;
    //     request.referenceType = requestType.referenceType;

    //     let formData: FormData = new FormData();
    //     formData.append('templateId', request.templateId);
    //     formData.append('locations', request.locations);
    //     formData.append('name', request.name);
    //     formData.append('referenceName', request.referenceName);
    //     formData.append(
    //       'requestJsonData',
    //       JSON.stringify(this.fandCRequestData)
    //     );
    //     formData.append('referenceType', request.referenceType);

    //     this.files.forEach((f) => {
    //       formData.append('files', f, f.name);
    //     });

    //     this.isLoading = true;

    //     const response = await lastValueFrom(
    //       this.taskDataService.insertTaskAsFormData(formData)
    //     );
    //     sendNotificationMessage(
    //       `${response[1].data[0].task.taskId}} task created`,
    //       this.messageService
    //     );

    //     this.isLoading = false;

    //     this.formElement.nativeElement.reset();
    //     this.formElement.nativeElement.submit();
    //   } catch (err: any) {
    //     this.isLoading = false;
    //     this.loggingService.logError(err, SeverityLevel.Error);
    //     notify(err.message, 'error', getNotificationTimeout());
    //   }
    // }
  }

  addPrototypeTasks() {
    const currentReferenceDate = (this.fandCRequestData as any)?.referenceDate;
    if (currentReferenceDate) {
      this.store.dispatch(
        setReferenceDate({ selectedReferenceDate: currentReferenceDate })
      );
    }

    const currentMaturityDate = (this.fandCRequestData as any)?.maturityDate;
    if (currentMaturityDate) {
      this.store.dispatch(
        setMaturityDate({ selectedMaturityDate: currentMaturityDate })
      );
    }

    const currentLoanNumber = (this.fandCRequestData as any)?.loanNumber;
    if (currentLoanNumber) {
      this.store.dispatch(
        setLoanNumber({ selectedLoan: currentLoanNumber as any })
      );
    }

    this.store.dispatch(addToSelectedPrototypeTasks());
  }

  saveGenerationTask() {
    // Set loading flag for saving generation task
    this.isSavingGenerationTask = true;
    // First save metadata which will trigger the saveGenerationTask effect
    this.store.dispatch(saveGenerationMetadata());
  }

  generateTasks() {
    this.isGeneratingTasks = true;
    this.store.dispatch(generateTasks());
  }

  saveTasks() {
    this.isSavingTasks = true;
    this.store.dispatch(saveGeneratedTasks());
  }

  async  ngOnInit() {
    this.store.dispatch(resetFandCGenerator());

    this.store.dispatch(
      fetchRootTemplate({ templateName: this.taskTemplateName })
    );

    this.loadingActionsSubscription = this.actions$
      .pipe(
        ofType(
          fetchLoansSuccess,
          fetchGeneratedTasksSuccess,
          saveGeneratedTasksSuccess,
          fetchGenerationMetadataSuccess,
          saveGenerationTaskSuccess
        )
      )
      .subscribe((action) => {
        if (action.type === fetchLoansSuccess.type) {
          this.isLoadingLoanData = false;
        }
        if (action.type === fetchGeneratedTasksSuccess.type) {
          this.isGeneratingTasks = false;
          this.scrollToEnd();
        }
        if (action.type === saveGeneratedTasksSuccess.type) {
          this.isSavingTasks = false;
        }
        if (action.type === fetchGenerationMetadataSuccess.type) {
          // When metadata is fetched successfully, we'll update the isLoadingMetadata flag
          // when the loan data arrives in the loanNumberSubscription
          // console.log('Metadata fetched successfully');
        }
        if (action.type === saveGenerationTaskSuccess.type) {
          // Clear saving indicator when save operation completes
          this.isSavingGenerationTask = false;
          // console.log('Generation task saved successfully');

          // Show success notification to the user
          notify('Generation task saved successfully', 'success', getNotificationTimeout());
        }
      });

    // Only fetch loans if we don't have metadata
    if (!this.taskGenerationMetadata) {
      this.isLoadingLoanData = true;
      this.store.dispatch(fetchLoans());
    }

    this.store.dispatch(
      fetchTemplateTypes({ criteria: { referenceTypeName: 'FandC' } })
    );

    this.store.dispatch(fetchFrequencyOptions());
    this.store.dispatch(fetchFormatOptions());
    this.store.dispatch(fetchReportEntityOptions());

    // Initialize form data once before subscriptions begin pushing store values.
    // This prevents later default initialization from overwriting restored metadata values.
    this.initializeFandCRequestData();

    try {
      // Set up subscriptions for updating editor options and other component properties
      this.templateTypeSubscription = this.templateTypeData$.subscribe((items) => {
        this.templateTypeEditorOptions = {
          ...this.templateTypeEditorOptions,
          items: items,
        };
      });

      this.templateDataSubscription = this.templateData$.subscribe((items) => {
        const mutableItems = Array.isArray(items) ? items.map(item => item.name) : [];
        let selectedValue = null;
        if (this.selectedTemplate && mutableItems.length > 0) {
          // Since mutableItems is now an array of strings (templateType values),
          // we need to check if selectedTemplate is in the array
          selectedValue = mutableItems.includes(this.selectedTemplate) ? this.selectedTemplate : null;
        }
        this.templateEditorOptions = {
          ...this.templateEditorOptions,
          items: mutableItems,
          value: selectedValue,
        };
      });

      this.defaultTasksForTemplateSubscription = this.defaultTasksForTemplate$.subscribe((result) => {
        // console.log(result);
        const sortedItems = (result || []).slice().sort((a: any, b: any) => {
          if (a.taskName < b.taskName) return -1;
          if (a.taskName > b.taskName) return 1;
          return 0;
        });
        this.defaultTasksEditorOptions = {
          ...this.defaultTasksEditorOptions,
          items: sortedItems,
        };
      });

      this.frequencyOptionsSubscription = this.frequencyOptions$.subscribe((items) => {
        // items is an array of strings, so just clone the array (no need to map/spread objects)
        const mutableItems = Array.isArray(items) ? [...items] : [];
        // console.log('Frequency Options:', mutableItems);
        this.frequencyEditorOptions = {
          ...this.frequencyEditorOptions,
          items: mutableItems,
        };
      });

      this.frequencyOffsetOptionsSubscription = this.frequencyOffsetOptions$.subscribe((items) => {
        const mutableItems = (items || []).map((x) => ({ ...x }));
        // Get the primitive value from your state (e.g., via a selector or local variable)
        const selectedValue = this.selectedFrequencyOffsetValue; // <-- primitive, not object
        this.frequencyOffsetEditorOptions = {
          ...this.frequencyOffsetEditorOptions,
          items: mutableItems,
          value: selectedValue,
        };
      });

      this.maturityDateSubscription = this.maturityDate$.subscribe((date) => {
        const currentMaturityDate = (this.fandCRequestData as any)?.maturityDate ?? null;
        const nextMaturityDate = date ?? currentMaturityDate;

        this.maturityDateOptions = {
          ...this.maturityDateOptions,
          value: nextMaturityDate,
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          maturityDate: nextMaturityDate,
        } as any;
      });

      this.formatOptionsSubscription = this.formatOptions$.subscribe((items) => {
        this.formatEditorOptions = {
          ...this.formatEditorOptions,
          items: items,
        };
      });

      this.reportEntityOptionsSubscription = this.reportEntityOptions$.subscribe((items) => {
        const currentValue = (this.selectedReportEntityValue ?? this.reportEntityOptions?.value ?? '')
          .toString()
          .trim();
        const fallbackLabelMap: Record<string, string> = {
          borrowers: 'Borrower',
          guarantors: 'Guarantor',
          jointVentures: 'Joint Venture',
          lenders: 'Lender',
          loanParties: 'Loan Parties',
          masterTenants: 'Master Tenant',
          operatingLessees: 'Operating Lessee',
          properties: 'Property',
        };

        const incomingItems = Array.isArray(items) ? items : [];
        const hasCurrentValue = currentValue
          ? incomingItems.some((item: any) => item?.value === currentValue)
          : true;

        const mergedItems = !hasCurrentValue && currentValue
          ? [
              ...incomingItems,
              {
                label: fallbackLabelMap[currentValue] ?? currentValue,
                value: currentValue,
              },
            ]
          : incomingItems;

        this.reportEntityOptions = {
          ...this.reportEntityOptions,
          items: mergedItems,
        };
      });

      this.entityNameOptionsSubscription = this.entityNameOptions$.subscribe((items) => {
        this.entityNameOptions = {
          ...this.entityNameOptions,
          items: items,
        };

        // Update customEntityNameOptions with the same items
        this.customEntityNameOptions = {
          ...this.customEntityNameOptions,
          items: items,
        };
      });

      this.loanDataSubscription = this.loanData$.subscribe((items) => {
        this.availableLoans = Array.isArray(items) ? items : [];
        // console.log('Loan Data:', items);
      });

      this.referenceDateSubscription = this.referenceDate$.subscribe((date) => {
        const currentReferenceDate = (this.fandCRequestData as any)?.referenceDate ?? null;
        const nextReferenceDate = date ?? currentReferenceDate;

        this.referenceDateOptions = {
          ...this.referenceDateOptions,
          value: nextReferenceDate,
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          referenceDate: nextReferenceDate,
        } as any;
      });

      // Add subscriptions to sync actual values from store to editor options
      this.templateTypeValueSubscription = this.store.select(selectSelectedTemplateType).subscribe((templateType) => {
        this.templateTypeEditorOptions = {
          ...this.templateTypeEditorOptions,
          value: templateType
        };
      });

      this.categoryValueSubscription = this.store.select(selectCategory).subscribe((category) => {
        this.templateEditorOptions = {
          ...this.templateEditorOptions,
          value: category
        };
      });

      this.frequencyValueSubscription = this.store.select(selectedFrequency).subscribe((frequency) => {
        this.selectedFrequency = frequency;
        this.frequencyEditorOptions = {
          ...this.frequencyEditorOptions,
          value: frequency
        };
      });

      this.frequencyOffsetValueSubscription = this.store.select(selectFrequencyOffsetValue).subscribe((offsetValue) => {
        this.selectedFrequencyOffsetValue = offsetValue;
        this.frequencyOffsetEditorOptions = {
          ...this.frequencyOffsetEditorOptions,
          value: offsetValue
        };
        this.oneTimeDueDateOptions = {
          ...this.oneTimeDueDateOptions,
          value: (offsetValue && typeof offsetValue === 'object' && 'getTime' in (offsetValue as any)) ? offsetValue as Date : null
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          frequencyOffsetOption:
            offsetValue && !(typeof offsetValue === 'object' && 'getTime' in (offsetValue as any))
              ? offsetValue
              : null,
          oneTimeDueDate:
            offsetValue && typeof offsetValue === 'object' && 'getTime' in (offsetValue as any)
              ? (offsetValue as Date)
              : null,
        } as any;
      });

      this.formatValueSubscription = this.store.select(selectSelectedFormatOption).subscribe((format) => {
        this.formatEditorOptions = {
          ...this.formatEditorOptions,
          value: format
        };
      });

      this.reportEntityValueSubscription = this.store.select(selectSelectedReportEntity).subscribe((reportEntity) => {
        const existingValue =
          (this.selectedReportEntityValue ?? (this.fandCRequestData as any)?.reportEntity ?? null) as string | null;
        const safeReportEntity =
          reportEntity == null || reportEntity === '' ? existingValue : reportEntity;

        this.selectedReportEntityValue = safeReportEntity;
        const normalizedValue = (safeReportEntity ?? '').toString().trim();
        const fallbackLabelMap: Record<string, string> = {
          borrowers: 'Borrower',
          guarantors: 'Guarantor',
          jointVentures: 'Joint Venture',
          lenders: 'Lender',
          loanParties: 'Loan Parties',
          masterTenants: 'Master Tenant',
          operatingLessees: 'Operating Lessee',
          properties: 'Property',
        };

        const existingItems = Array.isArray(this.reportEntityOptions?.items)
          ? this.reportEntityOptions.items
          : [];

        const hasValueInItems = normalizedValue
          ? existingItems.some((item: any) => item?.value === normalizedValue)
          : true;

        const mergedItems = !hasValueInItems && normalizedValue
          ? [
              ...existingItems,
              {
                label: fallbackLabelMap[normalizedValue] ?? normalizedValue,
                value: normalizedValue,
              },
            ]
          : existingItems;

        this.reportEntityOptions = {
          ...this.reportEntityOptions,
          items: mergedItems,
          value: safeReportEntity
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          reportEntity: safeReportEntity,
        } as any;
      });

      this.entityNameValueSubscription = this.store.select(selectSelectedEntityName).subscribe((entityName) => {
        this.customEntityNameOptions = {
          ...this.customEntityNameOptions,
          value: entityName
        };
      });

      // set values from metadata if available
      this.setStateFromMetadata();
    } catch (err: any) {
      this.loggingService.logError(err, SeverityLevel.Error);
      notify(err.message, 'error', getNotificationTimeout());
    }
  }

  private initializeFandCRequestData(): void {
    this.fandCRequestData = new TreasuryRequestData();
    // Add additional properties that are used in the form but not in the base interface.
    (this.fandCRequestData as any).oneTimeDueDate = null;
    (this.fandCRequestData as any).selectedTemplateType = null;
    (this.fandCRequestData as any).category = null;
    (this.fandCRequestData as any).frequency = null;
    (this.fandCRequestData as any).frequencyOffsetOption = null;
    (this.fandCRequestData as any).format = null;
    (this.fandCRequestData as any).reportEntity = null;
    (this.fandCRequestData as any).entityName = null;
    (this.fandCRequestData as any).referenceDate = null;
    (this.fandCRequestData as any).maturityDate = null;
    (this.fandCRequestData as any).taskTypes = null;
  }

  setStateFromMetadata() {
    // Set state from metadata if present
    if (this.taskGenerationMetadata) {
      // Set loading flag while fetching metadata
      this.isLoadingMetadata = true;

      this.store.dispatch(setTaskId({ taskId: this.taskGenerationMetadata.taskId }));
      this.store.dispatch(fetchGenerationMetadata({ metadataId: this.taskGenerationMetadata.taskGenerationMetadataId }));

      // If we have metadata, the loan number should be populated
      this.isLoanNumberPopulated = true;

      // The selected task types will be populated via the store subscription
      // when the metadata is loaded

      // if (meta.selectedLoanNumber) {
      //   this.store.dispatch(
      //     setLoanNumber({ selectedLoan: meta.selectedLoanNumber })
      //   );
      // }

      // if (meta.selectedPrototypeTasks) {
      //   this.store.dispatch(
      //     setSelectedPrototypeTasks({ selectedPrototypeTasks: meta.selectedPrototypeTasks })
      //   );
      // }
    }
  }

  scrollToEnd() {
    if (this.scrollView && this.scrollView.instance) {
      this.scrollView.instance.scrollTo({ top: Number.MAX_SAFE_INTEGER });
    }
  }

  ngOnDestroy() {
    // Clean up all subscriptions to prevent memory leaks
    this.unsubscribeIfExists(this.loadingActionsSubscription);
    this.unsubscribeIfExists(this.selectedPrototypeTasksSubscription);
    this.unsubscribeIfExists(this.generatedTasksSubscription);
    this.unsubscribeIfExists(this.loanNumberSubscription);
    this.unsubscribeIfExists(this.defaultTasksSubscription);
    this.unsubscribeIfExists(this.templateTypeSubscription);
    this.unsubscribeIfExists(this.templateDataSubscription);
    this.unsubscribeIfExists(this.defaultTasksForTemplateSubscription);
    this.unsubscribeIfExists(this.frequencyOptionsSubscription);
    this.unsubscribeIfExists(this.frequencyOffsetOptionsSubscription);
    this.unsubscribeIfExists(this.maturityDateSubscription);
    this.unsubscribeIfExists(this.formatOptionsSubscription);
    this.unsubscribeIfExists(this.reportEntityOptionsSubscription);
    this.unsubscribeIfExists(this.entityNameOptionsSubscription);
    this.unsubscribeIfExists(this.loanDataSubscription);
    this.unsubscribeIfExists(this.referenceDateSubscription);

    // Clean up value sync subscriptions
    this.unsubscribeIfExists(this.templateTypeValueSubscription);
    this.unsubscribeIfExists(this.categoryValueSubscription);
    this.unsubscribeIfExists(this.frequencyValueSubscription);
    this.unsubscribeIfExists(this.frequencyOffsetValueSubscription);
    this.unsubscribeIfExists(this.formatValueSubscription);
    this.unsubscribeIfExists(this.reportEntityValueSubscription);
    this.unsubscribeIfExists(this.entityNameValueSubscription);
  }

  // Helper method to safely unsubscribe
  private unsubscribeIfExists(subscription: Subscription): void {
    if (subscription) {
      subscription.unsubscribe();
    }
  }

  onFormDataChanged(event: any): void {
    // console.log(event);
    // Handle form data changes here if needed
    if (!event?.event) {
      return;
    }

    if (event && event.dataField === 'referenceDate') {
      if (event.value == null) {
        return;
      }
      this.store.dispatch(
        setReferenceDate({ selectedReferenceDate: event.value })
      );
      // console.log('referenceDate value:', event.value);
    }

    if (event && event.dataField === 'maturityDate') {
      if (event.value == null) {
        return;
      }
      this.store.dispatch(
        setMaturityDate({ selectedMaturityDate: event.value })
      );
      // console.log('maturityDate value:', event.value);
    }

    if (event && event.dataField === 'loanNumber') {
      const selectedItem = event.component?.option('selectedItem');
      const normalizedValue = (event.value ?? '').toString().trim().toLowerCase();
      const resolvedLoan = this.availableLoans.find((loan: any) => {
        const candidateValues = [
          loan?.name,
          loan?.Name,
          loan?.loanNumber,
          loan?.LoanNumber,
          loan?.referenceName,
          loan?.ReferenceName,
        ];

        return candidateValues
          .filter((value) => value != null)
          .some((value) => value.toString().trim().toLowerCase() === normalizedValue);
      });

      const selectedLoan = selectedItem ?? resolvedLoan ?? event.value;

      const selectedLoanId =
        selectedLoan?.id ??
        selectedLoan?.Id ??
        selectedLoan?.facilityId ??
        selectedLoan?.FacilityId ??
        resolvedLoan?.id ??
        resolvedLoan?.Id ??
        resolvedLoan?.facilityId ??
        resolvedLoan?.FacilityId ??
        null;

      this.store.dispatch(setLoanNumber({ selectedLoan: selectedLoan }));
      if (selectedLoanId) {
        this.store.dispatch(fetchLoanDetail({ facilityId: selectedLoanId }));
      }
      // console.log('loanNumber value:', event.value);
      this.isLoanNumberPopulated = !!selectedLoan;
    }
  }

  onShowInactiveLoansChanged = (e: any) => {
    this.showInactiveLoans = e.value;

    // Defer the BehaviorSubject update to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.showInactiveLoansSubject.next(e.value);
    }, 0);
  }

  onExporting(e) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Client Compliance Report');

    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      autoFilterEnabled: true,
      topLeftCell: { row: 2, column: 1 },
                  customizeCell: ({ gridCell, excelCell }) => {
              if (gridCell.rowType === "header") {
                // Dark grey header background with white text
                excelCell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FF4F4F4F" }, // Dark grey
                };
                excelCell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // White text
                excelCell.alignment = {
                  horizontal: "center",
                  vertical: "middle",
                };
              }
              if (gridCell.rowType === "data") {
                // Check if this is a date column that needs special formatting
                if (gridCell.column &&
                  (gridCell.column.dataField === 'referenceDate' ||
                   gridCell.column.dataField === 'firstDueDate')) {
                  this.setDateCell(gridCell, excelCell);
                }
                // Alternate row shading for data rows
                if (excelCell.row % 2 === 0) {
                  // Light grey for even rows
                  excelCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFEFEFEF" }, // Light grey
                  };
                } else {
                  // White for odd rows (default)
                  excelCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFFFFFFF" }, // White
                  };
                }
                excelCell.alignment = {
                  horizontal: "left",
                  vertical: "middle",
                };
              }
            },

    }).then((cellRange) => {
      // header
      const headerRow = worksheet.getRow(1);
      headerRow.height = 30;
      worksheet.mergeCells(1, 1, 1, 8);

      headerRow.getCell(1).value = 'Client Compliance Report';
      headerRow.getCell(1).font = { name: 'Segoe UI Light', size: 22 };
      headerRow.getCell(1).alignment = { horizontal: 'center' };

      // footer
      const footerRowIndex = cellRange.to.row + 2;
      const footerRow = worksheet.getRow(footerRowIndex);
      worksheet.mergeCells(footerRowIndex, 1, footerRowIndex, 8);

      footerRow.getCell(1).value = 'CreditHub TaskManager - MountStreet ';
      footerRow.getCell(1).font = { color: { argb: 'BFBFBF' }, italic: true };
      footerRow.getCell(1).alignment = { horizontal: 'right' };
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        const now = new Date();
        const formattedDate = now.getFullYear() + '_' +
          String(now.getMonth() + 1).padStart(2, '0') + '_' +
          String(now.getDate()).padStart(2, '0');

        saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
          `Client Compliance Report_${this.loanDisplayText}_${formattedDate}.xlsx`);
      });
    });
    e.cancel = true;
    //this.loggingService.logApplicationEvent(APPLICATION_EVENTS.EXPORT_TO_EXCEL, { reportName: "property-overview" });
  }

  private setDateCell(gridCell, excelCell: any) {
    if (gridCell.value instanceof Date) {
      const utcDate = new Date(Date.UTC(
        gridCell.value.getFullYear(),
        gridCell.value.getMonth(),
        gridCell.value.getDate()
      ));
      excelCell.numFmt = "MMM DD YYYY"; // Excel date format
      excelCell.value = utcDate;
    }
    else if (typeof gridCell.value === "string") {
      // Try to parse the date string
      const parsedDate = new Date(gridCell.value);
      // Check if the parsed date is valid
      if (isNaN(parsedDate.getTime())) {
        excelCell.value = gridCell.value; // Keep original value if parsing fails
      } else {
        excelCell.value = parsedDate;
      }
      excelCell.numFmt = "DD MMM YYYY"; // Excel date format
      excelCell.value = parsedDate;
    } else {
      excelCell.value = gridCell.value;
    }
  }

  // Custom item template function for loan dropdown
  loanItemTemplate = (itemData: any, itemIndex: number, itemElement: any) => {
    const cssClass = itemData.status === 'Inactive' ? 'inactive-item' : 'active-item';
    const displayText = `${itemData.name} (${itemData.status})`;
    itemElement.innerHTML = `<div class="${cssClass}">${displayText}</div>`;
    return itemElement;
  }
}
